(ns ui.view.screening.sub
  (:require
   [common.entity :as entity]
   [common.schema.entities.screening-profile.materialization :as sp-materialization]
   [re-frame.core :as rf]
   [ui.data.view-entities.sub :as view-entities-sub]
   [ui.state.session.sub :as session-sub]))

;; Two callers, two different stores of truth for the referenced entities:
;; - the legacy screening grid (frontend.view.grid.screening.view) calls
;;   these subs with just a screening-profile-id, relying on the entity
;;   (and everything it points at) already being in the global session
;;   cache via bulk-sync/legacy sync.
;; - the new-grid screening-profile NEV (frontend.view.nev.screening-profile.view)
;;   fetches the entity graph into a per-view SCOPE-LOCAL store instead
;;   (ui.data.view-entities.event/open-view-entity-scope) - that store is
;;   never merged into the global session cache, so looking the
;;   screening-profile (and its :screening-profile/kyc-profiles refs) up in
;;   the global cache here silently found nothing and rendered these
;;   modules as empty, even when the data was right there in the scope
;;   (see ai/mistakes.md). Accepting an optional [view-id scope-id] lets the
;;   NEV opt into the scope-local store while the legacy grid's call shape
;;   (and behavior) is completely unchanged.
(defn- entities-signals
  [view-id scope-id]
  (if (and view-id scope-id)
    [(rf/subscribe [::view-entities-sub/scope-entities-by-id view-id scope-id])]
    [(rf/subscribe [::session-sub/entities])
     (rf/subscribe [::session-sub/legacy-entities])]))

(defn- merge-entities
  [signals view-id scope-id]
  (if (and view-id scope-id)
    (first signals)
    (apply merge signals)))

(rf/reg-sub
 ::kyc-profiles-and-pep-statuses
 (fn [[_ _screening-profile-id view-id scope-id] _]
   (entities-signals view-id scope-id))
 (fn [signals [_ screening-profile-id view-id scope-id]]
   (let [entities (merge-entities signals view-id scope-id)
         entity-lookup (fn [id]
                         (get entities id))
         display-string (fn [& args]
                          (apply entity/display-string args))
         screening-profile (entity-lookup screening-profile-id)]
     (when screening-profile
       (sp-materialization/screening-profile->kyc-profiles-and-pep-statuses
        {:screening-profile screening-profile
         :entity-lookup     entity-lookup
         :display-string    display-string})))))

(rf/reg-sub
 ::counterparty-diligences-and-pep-statuses
 (fn [[_ _screening-profile-id view-id scope-id] _]
   (entities-signals view-id scope-id))
 (fn [signals [_ screening-profile-id view-id scope-id]]
   (let [entities (merge-entities signals view-id scope-id)
         entity-lookup (fn [id]
                         (get entities id))
         display-string (fn [& args]
                          (apply entity/display-string args))
         screening-profile (entity-lookup screening-profile-id)]
     (when screening-profile
       (sp-materialization/screening-profile->counterparty-diligences-and-pep-statuses
        {:screening-profile screening-profile
         :entity-lookup     entity-lookup
         :display-string    display-string})))))

(rf/reg-sub
 ::pep-or-rca-mismatch-data
 (fn [[_ screening-profile-id view-id scope-id] _]
   (into (entities-signals view-id scope-id)
         [(rf/subscribe [::kyc-profiles-and-pep-statuses screening-profile-id view-id scope-id])
          (rf/subscribe [::counterparty-diligences-and-pep-statuses screening-profile-id view-id scope-id])]))
 (fn [signals [_ screening-profile-id view-id scope-id]]
   (let [entities-signal-count (if (and view-id scope-id) 1 2)
         entities (merge-entities (take entities-signal-count signals) view-id scope-id)
         [kyc-profiles-and-pep-statuses
          counterparty-diligences-and-pep-statuses] (drop entities-signal-count signals)
         screening-profile (get entities screening-profile-id)]
     (when screening-profile
       (sp-materialization/calculate-pep-or-rca-mismatch-data
        {:screening-profile                        screening-profile
         :kyc-profiles-and-pep-statuses            kyc-profiles-and-pep-statuses
         :counterparty-diligences-and-pep-statuses counterparty-diligences-and-pep-statuses})))))
